import React, {useEffect, useState} from 'react';
import {connect} from "react-redux";
import Loader from "./layout/loading/loader";
import AodIcon from '@mui/icons-material/Aod';
import PageTitle from "./layout/PageTitle";
import {useLocation, useParams, useSearchParams} from "react-router-dom";
import {conteneursActions} from "../../modules/conteneurs/conteneurs.actions";
import InterventionTable from "./interventions/InterventionTable";
import {synthesesActions} from "../../modules/syntheses/syntheses.actions";
import InterventionView from "./interventions/InterventionView";
import {Box, Button, Card, CardContent, CardHeader, Chip, Divider, Stack, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import InterventionViewV2 from "./interventions/v2/InterventionViewV2";

import {patrimoinesActions} from "../../modules/patrimoines/patrimoines.actions";
import {helper} from "../../utils/helper";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {ConteneurIcon} from "../../utils/ui/Icons/ConteneurIcon.js";

/**
 * Toggle V1 / V2 de la vue intervention (INT-02) — temporaire, le temps de valider la V2 sur les
 * 6 variantes du handoff. Persisté par navigateur ; à retirer à la bascule définitive.
 */
const VIEW_VERSION_KEY = 'interventionView.version';
const readViewVersion = () => {
    try {
        return localStorage.getItem(VIEW_VERSION_KEY) === 'v1' ? 'v1' : 'v2';
    } catch (e) {
        return 'v2';
    }
};

function InterventionsPage({dispatch, ...props}) {
    const params = useParams();
    const [viewVersion, setViewVersion] = useState(readViewVersion);
    const onViewVersionChange = (e, value) => {
        if (!value) return;
        setViewVersion(value);
        try {
            localStorage.setItem(VIEW_VERSION_KEY, value);
        } catch (err) { /* stockage indisponible : le choix ne survit pas au rechargement */ }
    };
    const versionToggle = (
        <ToggleButtonGroup size="small" exclusive value={viewVersion} onChange={onViewVersionChange} aria-label="Version de la vue">
            <ToggleButton value="v1">Vue V1</ToggleButton>
            <ToggleButton value="v2">Vue V2</ToggleButton>
        </ToggleButtonGroup>
    );
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const isForPatrimoine = location.pathname.includes("patrimoine");
    const sourceInterventions = isForPatrimoine ? props.patrimoine?.interventions : props.conteneur?.interventions;
    // `?intervention=ID` (PAT-04 R10, micro-card carte) : ouvre cette intervention plutôt que la dernière.
    const wantedId = searchParams.get('intervention');
    const wanted = wantedId ? (sourceInterventions || []).find((i) => String(i?.id) === String(wantedId)) : null;
    const [latestIntervention, ...restInterventions] = sourceInterventions || [];
    const firstIntervention = wanted ?? latestIntervention;

    useEffect(() => {
        if (!isForPatrimoine && !props.conteneur && !props.loading && !props.error) {
            dispatch(conteneursActions.fetchConteneur(params.id));
        } else if (isForPatrimoine && !props.patrimoine && !props.loading && !props.error) {
            dispatch(patrimoinesActions.fetchPatrimoineWithInterventions(params.id));
        }
    }, [props.conteneur, props.patrimoine, props.loading, props.error]);

    useEffect(() => {
        return () => {
            dispatch(conteneursActions.clearConteneurs());
        };
    }, [dispatch]);

    const onPDFClick = (interventionId, interventionDate) => {
        dispatch(synthesesActions.fetchInterventionPDF(interventionId, interventionDate));
    };

    const label = isForPatrimoine ? props.patrimoine?.libelle : props.conteneur?.nomenclature;
    const lieu = isForPatrimoine ? props.patrimoine?.lieu : `${props.conteneur?.adresse} ${props.conteneur?.commune}`;

    return (
        <React.Fragment>
	        { viewVersion !== 'v2' && <PageTitle title={`Dernière intervention : ${label}`} icon={<AodIcon/>}/>}

            {props.loading ? (
                <Loader/>
            ) : (
                <Stack spacing={4} sx={{mt: viewVersion !== 'v2' ? 4 : 0}}>

                    {firstIntervention && viewVersion === 'v2' && (
                        <Box>
                            <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 1.5}}>{versionToggle}</Box>
                            <InterventionViewV2 interventionId={firstIntervention.id} onPDFClick={onPDFClick}/>
                        </Box>
                    )}

                    {firstIntervention && viewVersion === 'v1' && (
                        <Card>
                            <CardHeader
                                title={label + " - " + lieu}
                                subheader={
                                <>
                                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                                        <Chip
                                            size='small'
                                            label={props.conteneur?.dechetType}
                                            sx={helper.getColorForDechetType(props.conteneur?.dechetType)}
                                        />
                                        <Chip
                                            size='small'
                                            label={props.conteneur?.conteneurType}
                                        />
                                        <Chip
                                            size='small'
                                            icon={<ConteneurIcon />}
                                            label={`${props.conteneur?.quantite}`}
                                        />
                                    </Box>
                                </>
                            }
                                action={
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        {versionToggle}
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => onPDFClick(firstIntervention.id, firstIntervention.dateEvenement)}
                                            startIcon={<PictureAsPdfIcon/>}
                                        >
                                            Télécharger la fiche PDF
                                        </Button>
                                    </Stack>
                                }
                            />
                            <CardContent>
                                <Typography variant={'h6'} sx={{mb:2}}>{'Dernière intervention : ' + helper.getFormattedDatetime(firstIntervention.dateEvenement)}</Typography>
                                <InterventionView
                                    intervention={firstIntervention}
                                    onPDFClick={onPDFClick}
                                    conteneur={props.conteneur}
                                    patrimoine={props.patrimoine}
                                    largeDisplay
                                />
                            </CardContent>
                        </Card>
                    )}

                    {restInterventions?.length > 0 && (
                        <Box>
                            <Typography variant="h6" gutterBottom>Historique des interventions</Typography>
                            <InterventionTable interventions={restInterventions} onPDFClick={onPDFClick}/>
                        </Box>
                    )}
                </Stack>
            )}
        </React.Fragment>
    );
}

const mapStateToProps = function (state) {
    return {
        conteneur: state.conteneurs.conteneur,
        patrimoine: state.patrimoines.patrimoine,
        loading: state.conteneurs.loading || state.patrimoines.loading,
        error: state.conteneurs.error || state.patrimoines.error
    }
}

export default connect(mapStateToProps)(InterventionsPage);
